/**
 * Role vocabulary + capability predicates — the FE's single source of truth for RBAC.
 *
 * The backend is the authorization boundary; these predicates only decide what the UI
 * *offers* (which nav items appear, which enrichment calls fire, which buttons render).
 * Keep them cheap and derivable so route guards, nav, and API clients can share one policy.
 *
 * Contract mirrors `pyxisrc_ai.db.models.Role` on the backend and the RBAC matrix in
 * `pyxis/api/tests/test_rbac_matrix.py`:
 *   - admin / reviewer / sme / viewer → internal PyxisRC staff, cross-client read
 *   - client                          → external, row-scoped to their engagements
 *
 * Import `Role` (the constant object) rather than typing role names as string literals.
 * Renaming a role or adding a new one is then a single edit in this file.
 */

// The wire vocabulary. Everything else in the FE ties back to these names.
export const Role = {
  ADMIN: "admin",
  REVIEWER: "reviewer",
  SME: "sme",
  VIEWER: "viewer",
  CLIENT: "client",
} as const

export type Role = (typeof Role)[keyof typeof Role]

/** Every known role, in the canonical display/order. */
export const ROLES: readonly Role[] = Object.values(Role)

/** Anything the backend considers internal staff. */
export const STAFF_ROLES: readonly Role[] = [Role.ADMIN, Role.REVIEWER, Role.SME, Role.VIEWER]

/** Who may be an engagement's Primary Client Contact: staff who act, never a client login. */
export const PRIMARY_CLIENT_CONTACT_ROLES: readonly Role[] = [Role.ADMIN, Role.REVIEWER, Role.SME]

/**
 * Who may hold a review. Mirrors the backend's `_ASSIGNABLE_ROLES`: a viewer is read-only and a
 * client is the counterparty, so the backend refuses to assign either.
 */
export const ASSIGNABLE_ROLES: readonly Role[] = [Role.ADMIN, Role.REVIEWER, Role.SME]

// ── Display vocabulary ───────────────────────────────────────────────────────
// The one place a role gets a human name; every screen imports this map (review-data.ts
// re-exports it). The labels follow the backend's own `_prettify_role` in services/email.py,
// which is what the invitation email shows: `sme` is "Specialist", `client` is "Client
// contact". The console and the emails a person receives therefore use the same word.

/** Short name for a role: badges, selects, the sidebar's account line. */
export const ROLE_LABEL: Record<Role, string> = {
  [Role.ADMIN]: "Admin",
  [Role.REVIEWER]: "Reviewer",
  [Role.SME]: "Specialist",
  [Role.VIEWER]: "Viewer",
  [Role.CLIENT]: "Client Contact",
}

/** One sentence on what a role can reach — shown wherever a role is chosen. */
export const ROLE_DESCRIPTION: Record<Role, string> = {
  [Role.ADMIN]: "Manages accounts, API keys, and client engagements.",
  [Role.REVIEWER]: "Runs reviews and dispositions findings.",
  [Role.SME]: "Confirms or rejects escalated findings.",
  [Role.VIEWER]: "Read-only across reviews.",
  [Role.CLIENT]: "Sees one engagement's reviews and reports, nothing else.",
}

/** Narrow an untyped role string (from JWT or session storage) into a known Role. */
export function toRole(value: string | null | undefined): Role | null {
  return (ROLES as readonly string[]).includes(value ?? "") ? (value as Role) : null
}

export function isStaff(role: Role | null | undefined): boolean {
  return role != null && STAFF_ROLES.includes(role)
}

export function isClient(role: Role | null | undefined): boolean {
  return role === Role.CLIENT
}

export function isAdmin(role: Role | null | undefined): boolean {
  return role === Role.ADMIN
}

// ── Capability predicates ────────────────────────────────────────────────────
// Prefer these over hand-rolled role checks. They compose the primitives above so
// changing the underlying policy is a one-line edit here, not a codebase-wide grep.

/** Can the caller list every client engagement? Admin-only on the backend. */
export function canListClients(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/** Can the caller list console users + service keys? Admin-only on the backend. */
export function canManageUsersAndKeys(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/** Can the caller read `GET /api/v1/users`? The whole router is admin-only on the backend. */
export function canListUsers(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/**
 * Can this role start a new review from the queue? Admin, reviewer and SME, as legacy's
 * `canOpenNewReview`. A viewer is read-only (the backend refuses them), and a client files
 * corrections from their own review rather than starting one here.
 */
export function canOpenNewReview(role: Role | null | undefined): boolean {
  return role === Role.ADMIN || role === Role.REVIEWER || role === Role.SME
}

/** Can a user with this role be assigned a review? */
export function canBeAssigned(role: Role | null | undefined): boolean {
  return role != null && ASSIGNABLE_ROLES.includes(role)
}

/** Can the caller see reviews across every client's engagements? */
export function canReadAllClients(role: Role | null | undefined): boolean {
  return isStaff(role)
}

/** Can the caller open the Development group — evaluation runs and Integrations (service keys,
 *  API documentation)? Admin-only: it is development tooling, never shown to clients or to the
 *  rest of staff. The nav group and every route under it guard with this one predicate. */
export function canUseDevelopment(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/** Can the caller set a run's status, rename, note or delete it? The Development rule. */
export const canManageEvalRuns = canUseDevelopment

/** Can the caller leave a remark on an evaluation run's mark or finding? The Development rule:
 *  only admins open runs, so only admins write remarks. */
export const canRemarkOnEvalRun = canUseDevelopment

/** Can the caller decide whether an evaluation mark is in scope, or revert it to the AI's call?
 *  Admin-only on the server (others get 403), and it moves the run's figures. */
export function canSetMarkScope(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/** Can the caller open Integrations? The Development rule. */
export const canUseIntegrations = canUseDevelopment

/**
 * Can the caller record the client's response to a finding — the report's
 * "reviewed" tick and its dispute?
 *
 * The client's own axis, which staff may also mark on their behalf. Mirrors
 * `PATCH /reviews/{id}/findings/{fid}/client-resolution` (client, reviewer,
 * SME). Admin is offered it as well, as the legacy console did; a viewer is
 * read-only and is not.
 */
export function canSetClientResolution(role: Role | null | undefined): boolean {
  return role === Role.ADMIN || role === Role.REVIEWER || role === Role.SME || role === Role.CLIENT
}

/**
 * Can the caller take part in a review's conversation — post a message, and
 * mark a thread read when they open it?
 *
 * Mirrors `POST /reviews/{id}/comments` and `/comments/read` (reviewer, SME,
 * client; admin always passes). Not a viewer: reading is open to them, but
 * marking read is a write — read state is per side, so a viewer clearing a
 * thread would clear the reviewer's badge — and the server refuses it.
 */
export function canConverse(role: Role | null | undefined): boolean {
  return role === Role.ADMIN || role === Role.REVIEWER || role === Role.SME || role === Role.CLIENT
}

/**
 * Does the caller open a review in the workspace?
 *
 * Everyone but a client, who reads a review as its report: the workspace is
 * where staff work the findings. The route guard on `/reviews/:id` and every
 * link into a review read this, so a client is never shown a link the router
 * then turns around. Unknown (session still resolving) counts as staff, so a
 * staff member is not bounced on a cold load.
 */
export function opensReviewInWorkspace(role: Role | null | undefined): boolean {
  return !isClient(role)
}

/** Where opening a review takes the caller: the workspace for staff, the client report for a client. */
export function reviewLandingPath(
  role: Role | null | undefined,
  reviewId: string | number,
): string {
  const id = encodeURIComponent(String(reviewId))
  return opensReviewInWorkspace(role) ? `/reviews/${id}` : `/reviews/${id}/output`
}

/**
 * Can the caller file a corrected label as the next version of a review?
 *
 * Everyone but a viewer: a client's corrected upload goes through the same
 * intake, scoped server-side to their own engagement.
 */
export function canSubmitCorrectedLabel(role: Role | null | undefined): boolean {
  return role != null && role !== Role.VIEWER
}

/**
 * Can the caller choose a review's detector settings at intake, and read where
 * each applied setting came from?
 *
 * Staff. A client's intake is their corrected-version upload, which continues
 * its thread's settings, and "from engagement" or "deployment default" is
 * vocabulary of ours, not theirs.
 */
export function canChooseDetectorSettings(role: Role | null | undefined): boolean {
  return isStaff(role)
}
